import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    useTheme,
    Alert,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
} from '@mui/material';
import { Save, RestartAlt, Palette } from '@mui/icons-material';
import { BasicButton } from '../../../../../components/BasicButton';
import apiClient from '../../../../../services/api/ApiClient';
import { ApiEndpoints } from '../../../../../config/constants';
import { PermissionWrapper } from '../../../../../components/PermissionWrapper';

interface TintColorPickerProps {
    currentColor: string | null;
    fallbackColor: string;
    onColorChange: (newColor: string | null) => void;
    onPreviewChange?: (previewColor: string) => void;
}

// Preset colors for quick selection
const PRESET_COLORS = [
    { name: 'Purple', value: '#800080' },
    { name: 'Blue', value: '#1976d2' },
    { name: 'Teal', value: '#009688' },
    { name: 'Green', value: '#388e3c' },
    { name: 'Orange', value: '#f57c00' },
    { name: 'Red', value: '#d32f2f' },
    { name: 'Pink', value: '#c2185b' },
    { name: 'Indigo', value: '#303f9f' },
    { name: 'Black', value: '#212121' },
    { name: 'Dark Blue', value: '#1a237e' },
];

export const TintColorPicker: React.FC<TintColorPickerProps> = ({
    currentColor,
    fallbackColor,
    onColorChange,
    onPreviewChange
}) => {
    const theme = useTheme();
    const [isSaving, setIsSaving] = useState(false);
    const [color, setColor] = useState<string>(currentColor || fallbackColor);
    const [hasChanges, setHasChanges] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [useCustomColor, setUseCustomColor] = useState(!!currentColor);

    // Sync with parent prop
    useEffect(() => {
        const newColor = currentColor || fallbackColor;
        setColor(newColor);
        setUseCustomColor(!!currentColor);
    }, [currentColor, fallbackColor]);

    const handleColorChange = (newColor: string) => {
        setColor(newColor);
        setHasChanges(true);
        setUseCustomColor(true);
        // Notify parent immediately for live preview
        onPreviewChange?.(newColor);
    };

    const handlePresetSelect = (_event: React.MouseEvent<HTMLElement>, selectedColor: string | null) => {
        if (selectedColor) {
            handleColorChange(selectedColor);
        }
    };

    const handleCustomColorChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        // Validate hex color
        if (/^#[0-9A-Fa-f]{6}$/.test(value) || /^#[0-9A-Fa-f]{3}$/.test(value)) {
            handleColorChange(value);
        } else if (value.startsWith('#') && value.length <= 7) {
            // Allow typing
            setColor(value);
            setHasChanges(true);
            setUseCustomColor(true);
        }
    };

    const handleSave = async () => {
        // Validate color format
        if (!/^#[0-9A-Fa-f]{6}$/.test(color) && !/^#[0-9A-Fa-f]{3}$/.test(color)) {
            setError('Please enter a valid hex color (e.g., #800080)');
            return;
        }

        setIsSaving(true);
        setError(null);
        setSuccess(null);

        try {
            await apiClient.patch(
                ApiEndpoints.SplashScreen.UpdateSettings,
                { splashTintColor: useCustomColor ? color : null }
            );

            onColorChange(useCustomColor ? color : null);
            setSuccess('Tint color saved successfully!');
            setHasChanges(false);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to save tint color');
        } finally {
            setIsSaving(false);
        }
    };

    const handleReset = async () => {
        if (!window.confirm('Reset to use the store\'s primary color?')) {
            return;
        }

        setIsSaving(true);
        setError(null);
        setSuccess(null);

        try {
            await apiClient.patch(
                ApiEndpoints.SplashScreen.UpdateSettings,
                { splashTintColor: null }
            );

            setColor(fallbackColor);
            setUseCustomColor(false);
            onColorChange(null);
            setSuccess('Reset to primary color!');
            setHasChanges(false);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to reset tint color');
        } finally {
            setIsSaving(false);
        }
    };

    const displayColor = color || fallbackColor;

    return (
        <>
            {/* Alerts */}
            {error && (
                <Alert severity='error' sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}
            {success && (
                <Alert severity='success' sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
                    {success}
                </Alert>
            )}

            {/* Action Buttons */}
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                {!useCustomColor && (
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        bgcolor: theme.palette.info.main + '20',
                        color: theme.palette.info.main,
                        px: 1,
                        py: 0.25,
                        borderRadius: 1,
                        fontSize: '0.7rem',
                        mr: 'auto'
                    }}>
                        Using Primary Color
                    </Box>
                )}
                {useCustomColor && currentColor && (
                    <PermissionWrapper screen='splash-screen' permission='update'>
                        <BasicButton
                            title='Reset'
                            variant='outlined'
                            onClick={handleReset}
                            isLoading={isSaving}
                            startIcon={<RestartAlt fontSize='small' />}
                            sx={{
                                height: 32,
                                fontSize: '0.75rem',
                                px: 2,
                            }}
                        />
                    </PermissionWrapper>
                )}
                {hasChanges && (
                    <PermissionWrapper screen='splash-screen' permission='update'>
                        <BasicButton
                            title='Save'
                            variant='contained'
                            onClick={handleSave}
                            isLoading={isSaving}
                            startIcon={<Save fontSize='small' />}
                            sx={{
                                height: 32,
                                fontSize: '0.75rem',
                                px: 2,
                            }}
                        />
                    </PermissionWrapper>
                )}
            </Box>

            {/* Color Preview */}
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                    sx={{
                        width: 60,
                        height: 60,
                        borderRadius: 2,
                        bgcolor: displayColor,
                        border: `2px solid ${theme.palette.divider}`,
                        boxShadow: `0 4px 12px ${displayColor}40`,
                    }}
                />
                <Box>
                    <Typography variant='body2' sx={{ fontFamily: 'primaryFont', fontWeight: 600 }}>
                        Current Color
                    </Typography>
                    <Typography variant='caption' sx={{ color: theme.palette.text.secondary, fontFamily: 'primaryFont' }}>
                        {displayColor.toUpperCase()}
                    </Typography>
                </Box>
            </Box>

            {/* Preset Colors */}
            <Box sx={{ mb: 3 }}>
                <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{ mb: 1, display: 'block', fontFamily: 'primaryFont' }}
                >
                    Quick Colors
                </Typography>
                <ToggleButtonGroup
                    value={color}
                    exclusive
                    onChange={handlePresetSelect}
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 0.5,
                        '& .MuiToggleButtonGroup-grouped': {
                            border: `1px solid ${theme.palette.divider} !important`,
                            borderRadius: '8px !important',
                            m: 0.25,
                        }
                    }}
                >
                    {PRESET_COLORS.map((preset) => (
                        <Tooltip key={preset.value} title={preset.name} arrow>
                            <ToggleButton
                                value={preset.value}
                                sx={{
                                    width: 36,
                                    height: 36,
                                    p: 0,
                                    bgcolor: preset.value,
                                    '&:hover': {
                                        bgcolor: preset.value,
                                        opacity: 0.8,
                                    },
                                    '&.Mui-selected': {
                                        bgcolor: preset.value,
                                        border: `2px solid ${theme.palette.primary.main} !important`,
                                        boxShadow: `0 0 0 2px ${theme.palette.background.paper}, 0 0 0 4px ${theme.palette.primary.main}`,
                                    },
                                }}
                            >
                                <Box sx={{ width: '100%', height: '100%' }} />
                            </ToggleButton>
                        </Tooltip>
                    ))}
                </ToggleButtonGroup>
            </Box>

            {/* Custom Color Input */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Palette fontSize='small' color='action' />
                    <Typography variant='caption' color='text.secondary' sx={{ fontFamily: 'primaryFont' }}>
                        Custom Color
                    </Typography>
                </Box>
                <TextField
                    size='small'
                    value={color}
                    onChange={handleCustomColorChange}
                    placeholder='#800080'
                    sx={{
                        width: 120,
                        '& .MuiInputBase-input': {
                            fontFamily: 'monospace',
                            fontSize: '0.875rem',
                        }
                    }}
                    InputProps={{
                        startAdornment: (
                            <Box
                                sx={{
                                    width: 20,
                                    height: 20,
                                    borderRadius: 0.5,
                                    bgcolor: displayColor,
                                    mr: 1,
                                    border: `1px solid ${theme.palette.divider}`,
                                }}
                            />
                        ),
                    }}
                />
                <input
                    type='color'
                    value={displayColor}
                    onChange={(e) => handleColorChange(e.target.value)}
                    style={{
                        width: 36,
                        height: 36,
                        padding: 0,
                        border: `2px solid ${theme.palette.divider}`,
                        borderRadius: 8,
                        cursor: 'pointer',
                        backgroundColor: 'transparent',
                    }}
                />
            </Box>
        </>
    );
};
